/**
 * AG Grid cell editors that put the shared `ClientSearch` / `EntitySearch`
 * components inside an attribute row's Value cell, so editing a reference
 * attribute searches live data instead of picking from a static list.
 *
 * Written against AG Grid 31's **ref-based** editor contract: the component is a
 * `forwardRef` that exposes `getValue()`, and the grid reads that when editing
 * stops. (The newer `CustomCellEditorProps` / `onValueChange` shape is NOT what a
 * component reached through `cellEditorSelector` receives here — the grid hands it
 * legacy `ICellEditorParams` plus a ref, so a plain function component blows up
 * with "Function components cannot be given refs" and `onValueChange is not a
 * function".)
 *
 * Two more things this has to get right:
 *
 * 1. A pick reports an ID, but the cell's value is the display NAME. The name is
 *    held in an editor-local ref and handed over by `getValue()`; the ID goes
 *    out-of-band through `reportIds`, which writes to a ref in the owner. That
 *    split is deliberate — staging the id in React state on every keystroke would
 *    hand the grid a new `rowData` array mid-edit, and AG Grid closes the editor
 *    whose rows changed underneath it.
 *
 * 2. The editor mounts the search field WITHOUT `selectedName`, so the field never
 *    renders its preset chip. That chip is for the upload takeover, which remounts
 *    per file and needs a selection made under a different file to render itself; in
 *    a cell editor it is actively wrong — the cell already shows the value, and the
 *    chip's clear self-commits through the rule below, costing a second session.
 *
 * 3. `ClientSearch` resolves its display name asynchronously, so `onChange` fires
 *    twice for one pick: first with the raw id standing in for the name, then with
 *    the resolved name. Closing on the first would freeze the id into the cell, so
 *    the editor waits for a report whose name is no longer the id — then commits
 *    itself, which is what drops the picked value straight into the Value cell.
 *
 * These mount `@jsq/common-components` search components, which run their own
 * queries — hence the `referenceSearch` opt-in on `DocumentDetail`, which is only
 * set where an `ApolloProvider` is above the tree.
 */
import React, { forwardRef, useImperativeHandle, useRef } from "react";
import type { AgGrid } from "@jsq/ds/core";
import {
  ClientSearchField,
  EntitySearchField,
  InvestorPositionSearchField,
} from "../UploadDocumentModal/ReferenceSearchField";
import * as styles from "./styles_vanilla.css";
import type { MiniAttrCellEditorParams, MiniAttrRow } from "#common/MiniAttrGrid";
import type { Space } from "#common/types/vault";
import type { ReferenceSelection } from "../UploadDocumentModal/ReferenceSearchField";

/** What the editors need from the owning `DocumentDetail`. */
export type ReferenceEditorContext = {
  /** Active space — when scoped, limits the client picker to configured clients. */
  space?: Space | null;
  /**
   * Record the picked ids for a row, out-of-band. Must NOT trigger a render (see
   * the module note) — the owner writes them to a ref and reads them back when
   * the cell's value commits.
   */
  reportIds: (row: MiniAttrRow, ids: string[] | undefined) => void;
  /** The client currently in effect, which scopes the entity and investor searches. */
  clientId?: string;
  /** The entity currently in effect, which scopes the investor position search. */
  entityId?: string;
};

/**
 * The editor-local half of a pick: the name to hand back through `getValue`, and
 * whether this report is settled enough to commit and close.
 *
 * The name lives in a REF, not state. A settled pick reports and self-commits in
 * the same tick, so the grid calls `getValue()` before React has re-rendered — a
 * state-backed value would still be the previous one, which committed the OLD name
 * alongside the NEW reference id.
 */
function useReferenceSelection(params: MiniAttrCellEditorParams, ctx: ReferenceEditorContext) {
  const latestName = useRef(params.value ?? "");

  const onChange = (selection: ReferenceSelection) => {
    latestName.current = selection.name;
    if (params.data) ctx.reportIds(params.data, selection.id ? [selection.id] : undefined);
    // The id standing in for an unresolved name is not worth committing; anything
    // else — a resolved name, or a clear — is, so the cell updates on the spot.
    if (selection.name !== selection.id) params.stopEditing();
  };

  return { getValue: () => latestName.current, onChange };
}

/** Build the pair of editors bound to one context (see `DocumentDetail`). */
export function makeReferenceCellEditors(ctx: ReferenceEditorContext) {
  const ClientCellEditor = forwardRef<AgGrid.ICellEditor, MiniAttrCellEditorParams>(
    (params, ref) => {
      const { getValue, onChange } = useReferenceSelection(params, ctx);
      useImperativeHandle(ref, () => ({ getValue }), [getValue]);
      return (
        <div className={styles.refCellEditor}>
          {/* NO `selectedName`: it would render the field's preset chip instead of
              the live search box, and the chip's clear fires `onChange({name: ""})`,
              which self-commits — so changing an already-set Client took TWO edit
              sessions (clear + close, then reopen to search). The cell behind this
              popup already shows the current value; the editor's whole job is to
              replace it, so it opens straight onto the search box. */}
          <ClientSearchField
            label={params.data?.attribute ?? "client"}
            space={ctx.space ?? undefined}
            onChange={onChange}
          />
        </div>
      );
    },
  );
  ClientCellEditor.displayName = "ClientCellEditor";

  const EntityCellEditor = forwardRef<AgGrid.ICellEditor, MiniAttrCellEditorParams>(
    (params, ref) => {
      const { getValue, onChange } = useReferenceSelection(params, ctx);
      useImperativeHandle(ref, () => ({ getValue }), [getValue]);
      return (
        <div className={styles.refCellEditor}>
          {/* No `selectedName` — see the note on the client editor above. */}
          <EntitySearchField clientId={ctx.clientId} onChange={onChange} />
        </div>
      );
    },
  );
  EntityCellEditor.displayName = "EntityCellEditor";

  const InvestorCellEditor = forwardRef<AgGrid.ICellEditor, MiniAttrCellEditorParams>(
    (params, ref) => {
      const { getValue, onChange } = useReferenceSelection(params, ctx);
      useImperativeHandle(ref, () => ({ getValue }), [getValue]);
      return (
        <div className={styles.refCellEditor}>
          {/* No `selectedName` — see the note on the client editor above. */}
          <InvestorPositionSearchField
            clientId={ctx.clientId}
            entityId={ctx.entityId}
            onChange={onChange}
          />
        </div>
      );
    },
  );
  InvestorCellEditor.displayName = "InvestorCellEditor";

  return { ClientCellEditor, EntityCellEditor, InvestorCellEditor };
}
